/**
 * Portal motion layer (Nov 2026): the "living dashboard" detailing for the 2026 shell.
 *  - route enter: the page column dissolves in from a soft blur over the ambient backdrop (motion.css)
 *  - count-up: metric numbers roll from 0 to their formatted value the first time they scroll into view
 *  - nav pill: the active sidebar highlight slides between links instead of jumping
 * Cards, bars, rings and chart lines animate in CSS only (motion.css), so async content animates as it arrives.
 *
 * Everything here is progressive: no element is ever rendered with a fake value (React renders the real text; the
 * count-up only rewrites the same text node while it runs and stops the moment React changes it), and nothing runs when
 * the user or OS asks for reduced motion, or where the browser lacks matchMedia / IntersectionObserver (tests, old WebViews).
 */
import { useEffect, useLayoutEffect, type RefObject } from 'react';

/** Metric numbers that count up. Opt any element in with data-countup; opt out with data-countup="off". */
export const COUNTER_SELECTOR = [
  '.qm-kpi__number', '.qm-stat__value', '.qx-stat__value', '.qs-kv__value', '.qm-ar-stat__value', '.qs-pulse__big',
  '.qm-ring__label b', '[data-countup]',
].map((s) => `${s}:not([data-countup="off"])`).join(', ');

export interface ParsedNumber { prefix: string; value: number; decimals: number; grouped: boolean; suffix: string }

/**
 * Split a formatted metric ("$34,813", "2.1%", "AED 1,250.50", "-12", "65.47% uptime") into prefix / number / suffix.
 * Returns null for anything that is not a single plain number (dates, ranges, "3 of 20", non-Latin digits) so it is left alone.
 */
export function parseMetric(text: string): ParsedNumber | null {
  const m = /^([^\d\-−]*?)([-−]?)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?([^\d]*)$/.exec(text.trim());
  if (!m) return null;
  const [, prefix = '', sign = '', int = '', frac = '', suffix = ''] = m;
  // Prefix is a currency mark or code ("$", "AED "), never a word ("Nov 4"); suffix is a unit ("%", "K", "% uptime").
  if (!/^(?:[^\p{L}\d\s]{0,2}|[A-Z]{2,3}\s?)$/u.test(prefix) || suffix.length > 12) return null;
  const value = Number(`${sign ? '-' : ''}${int.replace(/,/g, '')}${frac ? `.${frac}` : ''}`);
  if (!Number.isFinite(value) || value === 0) return null;
  return { prefix, value, decimals: frac.length, grouped: int.includes(','), suffix };
}

export function formatMetric(p: ParsedNumber, v: number): string {
  const fixed = Math.abs(v).toFixed(p.decimals);
  const [i = '0', f] = fixed.split('.');
  const int = p.grouped ? i.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : i;
  return `${p.prefix}${v < 0 ? '-' : ''}${int}${f ? `.${f}` : ''}${p.suffix}`;
}

const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t));

/** True when animation is allowed: the <html data-motion> preference (set by the shell) wins, then the OS setting. */
export function motionAllowed(doc: Document = document): boolean {
  const pref = doc.documentElement.getAttribute('data-motion');
  if (pref === 'reduce') return false;
  if (pref === 'no-preference') return true;
  return typeof window.matchMedia === 'function' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function countUp(el: HTMLElement, duration = 1100) {
  const node = el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE ? (el.firstChild as Text) : null;
  if (!node) return;
  const final = node.nodeValue ?? '';
  const parsed = parseMetric(final);
  if (!parsed) return;
  let written = formatMetric(parsed, 0);
  node.nodeValue = written;
  const start = performance.now();
  const step = (now: number) => {
    if (node.nodeValue !== written) return; // React re-rendered the value: it owns the text again.
    const t = Math.min(1, (now - start) / duration);
    written = t >= 1 ? final : formatMetric(parsed, parsed.value * easeOutExpo(t));
    node.nodeValue = written;
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * Watches the page column: replays the route-enter animation on navigation and counts metric numbers up the first time
 * each one becomes visible (including numbers that arrive later from the BFF).
 */
export function useShellMotion(mainRef: RefObject<HTMLElement | null>, routeKey: string, motion: 'reduce' | 'no-preference') {
  // Route enter: restart the CSS animation on the page column without remounting React children.
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main || motion === 'reduce') return;
    main.removeAttribute('data-enter');
    void main.offsetWidth; // reflow so the animation restarts
    main.setAttribute('data-enter', 'true');
    const done = () => main.removeAttribute('data-enter');
    const timer = window.setTimeout(done, 900);
    return () => window.clearTimeout(timer);
  }, [mainRef, routeKey, motion]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main || motion === 'reduce' || typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined' || !motionAllowed()) return;
    const seen = new WeakSet<Element>();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);
        countUp(e.target as HTMLElement);
      }
    }, { threshold: 0.4 });
    const scan = (root: ParentNode) => {
      root.querySelectorAll<HTMLElement>(COUNTER_SELECTOR).forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        io.observe(el);
      });
    };
    scan(main);
    const mo = new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches(COUNTER_SELECTOR)) { if (!seen.has(n)) { seen.add(n); io.observe(n); } }
        scan(n);
      });
    });
    mo.observe(main, { childList: true, subtree: true });
    return () => { mo.disconnect(); io.disconnect(); };
  }, [mainRef, motion]);
}

/**
 * Sliding active pill for the sidebar: positions one indicator over the active link (CSS vars on the nav) and lets
 * motion.css transition it. The link's own active background stays as the fallback until the pill is placed.
 * `layoutKey` changes whenever the active link or the nav layout (rail collapse, open sections) changes.
 */
export function useNavIndicator(navRef: RefObject<HTMLElement | null>, layoutKey: string) {
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const place = () => {
      const list = nav.querySelector<HTMLElement>('.qm-drawer__list');
      const active = nav.querySelector<HTMLElement>('.qm-drawer__link[data-active="true"]');
      if (!list || !active || active.offsetParent === null) { nav.removeAttribute('data-pill'); return; }
      const lr = list.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      list.style.setProperty('--qm-pill-y', `${ar.top - lr.top}px`);
      list.style.setProperty('--qm-pill-x', `${ar.left - lr.left}px`);
      list.style.setProperty('--qm-pill-w', `${ar.width}px`);
      list.style.setProperty('--qm-pill-h', `${ar.height}px`);
      // First placement snaps (no slide in from the top); later moves animate.
      if (!nav.hasAttribute('data-pill')) requestAnimationFrame(() => nav.setAttribute('data-pill', 'on'));
    };
    place();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(place);
    ro.observe(nav);
    const list = nav.querySelector('.qm-drawer__list');
    if (list) ro.observe(list);
    return () => ro.disconnect();
  }, [navRef, layoutKey]);
}
